import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Save, Globe, EyeOff } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";
import { toast } from "sonner";
import { SUPPORTED_LANGS } from "@/i18n";

export default function ArticleEdit() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [html, setHtml] = useState("");
  const [text, setText] = useState("");
  const [status, setStatus] = useState("published");
  const [sourceLang, setSourceLang] = useState("en");
  const [isInternal, setIsInternal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);

  useEffect(() => {
    api.get("/kb/categories").then((r) => {
      setCategories(r.data);
      if (!category) setCategory(r.data[0]);
    });
  }, []);

  useEffect(() => {
    if (!isNew) {
      // CRITICAL: fetch the RAW source content (bypass translation cache) so admin
      // sees exactly what's stored, not a translated preview. Without ?raw=1 an
      // admin viewing in NL would edit the Dutch-translated version and save it
      // back as the new source, silently overwriting the original English text.
      api
        .get(`/kb/articles/${id}`, { params: { raw: 1 } })
        .then((r) => {
          const a = r.data;
          setTitle(a.title);
          setCategory(a.category);
          setTagsInput((a.tags || []).join(", "));
          setHtml(a.content_html);
          setText(a.content_text || "");
          setStatus(a.status || "published");
          setSourceLang(a.source_lang || "en");
          setIsInternal(!!a.is_internal);
        })
        .finally(() => setLoading(false));
    }
  }, [id, isNew]);

  const save = async (nextStatus) => {
    if (!title.trim() || !category) {
      toast.error(t("errors.generic"));
      return;
    }
    setSaving(true);
    const payload = {
      title: title.trim(),
      category,
      content_html: html,
      content_text: text,
      tags: tagsInput.split(",").map((tg) => tg.trim()).filter(Boolean),
      status: nextStatus || status,
      source_lang: sourceLang,
      is_internal: isInternal,
    };
    try {
      const r = isNew
        ? await api.post("/kb/articles", payload)
        : await api.put(`/kb/articles/${id}`, payload);
      toast.success(isNew ? t("articleEdit.articleSaved") : t("articleEdit.articleSaved"));
      navigate(`/knowledge-base/${r.data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-zinc-500 text-sm">{t("common.loading")}</div>;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Link
        to={isNew ? "/knowledge-base" : `/knowledge-base/${id}`}
        className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white"
        style={{ transitionProperty: "color", transitionDuration: "180ms" }}
      >
        <ArrowLeft className="w-4 h-4" /> {t("common.cancel")}
      </Link>

      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-2">
          {isNew ? t("articleEdit.createTitle") : t("articleEdit.editTitle")}
        </p>
        <input
          data-testid="article-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t("articleEdit.titlePlaceholder")}
          className="w-full bg-transparent border-0 outline-none font-display text-4xl font-light tracking-tight text-white placeholder:text-zinc-700"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2 block">{t("articleEdit.categoryLabel")}</label>
          <select
            data-testid="article-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="em-input"
          >
            {categories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2 block">{t("articleEdit.tagsLabel")}</label>
          <input
            data-testid="article-tags"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            className="em-input"
            placeholder={t("articleEdit.tagsPlaceholder")}
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2 flex items-center gap-1.5">
            <Globe className="w-3 h-3" /> {t("articleEdit.sourceLangLabel")}
          </label>
          <select
            data-testid="article-source-lang"
            value={sourceLang}
            onChange={(e) => setSourceLang(e.target.value)}
            className="em-input"
          >
            {SUPPORTED_LANGS.map((l) => (
              <option key={l.code} value={l.code}>{l.flag} {l.label}</option>
            ))}
          </select>
        </div>
      </div>

      <p className="text-[11px] text-zinc-600 -mt-2 leading-relaxed">
        {t("articleEdit.sourceLangHint", {
          lang: SUPPORTED_LANGS.find((l) => l.code === sourceLang)?.label || sourceLang,
        })}
      </p>

      <label
        htmlFor="article-internal-toggle"
        className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer group ${
          isInternal
            ? "border-gold/40 bg-gold/[0.04]"
            : "border-white/5 bg-white/[0.02] hover:border-white/10"
        }`}
        style={{ transitionProperty: "background-color, border-color", transitionDuration: "180ms" }}
      >
        <input
          id="article-internal-toggle"
          data-testid="article-internal"
          type="checkbox"
          checked={isInternal}
          onChange={(e) => setIsInternal(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-gold shrink-0"
        />
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm text-white font-medium">
            <EyeOff className="w-3.5 h-3.5 text-gold" strokeWidth={1.75} />
            {t("articleEdit.internalToggleTitle")}
          </div>
          <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
            {t("articleEdit.internalToggleHint")}
          </p>
        </div>
      </label>

      <RichTextEditor
        value={html}
        onChange={(h, tx) => {
          setHtml(h);
          setText(tx);
        }}
      />

      <div className="flex justify-end items-center gap-3">
        <div className="text-xs text-zinc-500 mr-auto flex items-center gap-2">
          <span className="uppercase tracking-widest">{t("common.status")}:</span>
          <span className={status === "draft" ? "text-amber-300" : "text-emerald-300"}>{status}</span>
        </div>
        <button
          data-testid="article-save-draft"
          onClick={() => save("draft")}
          disabled={saving}
          className="bg-white/5 hover:bg-white/10 text-white text-sm rounded-lg px-5 py-2.5 flex items-center gap-2 disabled:opacity-50"
          style={{ transitionProperty: "background-color", transitionDuration: "180ms" }}
        >
          {t("articleEdit.saveDraft")}
        </button>
        <button
          data-testid="article-save"
          onClick={() => save("published")}
          disabled={saving}
          className="bg-gold text-ink-0 font-semibold rounded-lg px-6 py-2.5 flex items-center gap-2 hover:bg-gold-hover disabled:opacity-50"
          style={{ transitionProperty: "background-color", transitionDuration: "180ms" }}
        >
          <Save className="w-4 h-4" /> {saving ? t("articleEdit.publishing") : t("articleEdit.publishNow")}
        </button>
      </div>
    </div>
  );
}
